import { catchError, map } from 'rxjs/operators';
import { ErrorService, Error } from '@app/services/error.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { toParams } from '@shared/utils/query-to-params';
import { App, EmbedApp, configuration, embed as embedForApp } from '@data/models/app.model';
import { FilterParams } from '@claranet/node-search-filter';
import { Embedded } from '@data/models/embed.model';
import { uniq } from 'lodash';
import { TagService } from '@shared/components/tag/tag.service';
import { Tag } from '@data/models/tag.model';

export const APP_SERVICE_API_ROOT = '/core/v1/apps';

function isApplicationIdentifier(id: string): boolean {
    return Boolean(id) && id.startsWith('APP-') && id.length === 10;
}
@Injectable()
export class AppService implements TagService {

    private errors: Error[];

    constructor(
        private errorService: ErrorService,
        private http: HttpClient
    ) {
        this.errors = this.errorService.appServiceErrors;
    }

    public fetchTags({ filter }: FilterParams): Observable<Tag[]> {
        return of([{ name: filter, count: 0 }]);
    }

    public getApp(appId: string, { includeRemoved = false }: { includeRemoved?: boolean } = {}): Observable<App | null> {
        if (isApplicationIdentifier(appId) === false) {
            return of(null);
        }
        const params = toParams({ showDeleted: `${includeRemoved}` });
        return this.http.get<App>(`${APP_SERVICE_API_ROOT}/${appId}`, { params }).pipe(
            map((app) => new App(app)),
            catchError(this.errorService.errorHandler<App>(this.errors, 'Récupération de l\'application.', null))
        );
    }

    public getEmbeddedApp<T extends embedForApp>(appId: string, embed: T[] = []): Observable<EmbedApp<T> | null> {
        if (isApplicationIdentifier(appId) === false) {
            return of(null);
        }
        const embeds = uniq(embed.map((it) => configuration[it].embed)).join(',');
        return this.http.get<App & { embedded: Embedded }>(APP_SERVICE_API_ROOT + '/' + appId, { params: { embed: embeds } }).pipe(
            map((cell) => App.toCustomApp(cell, cell.embedded, embed)),
            catchError(this.errorService.errorHandler<EmbedApp<T>>(this.errors, 'Récupération des données de cellule', null))
        );
    }

    public findApps(query?: FilterParams): Observable<App[]> {
        const params = toParams(query);
        return this.http.get<{ apps: App[]; embedded: Embedded }>(APP_SERVICE_API_ROOT, { params }).pipe(
            map(({ apps }) =>
                apps.map((businessProject) => new App(businessProject))
            ),
            catchError(this.errorService.errorHandler<App[]>(this.errors, 'Récupération des applications', []))
        );
    }

    public findProjectApps(projectId: string, query?: FilterParams): Observable<App[]> {
        const queryParams = toParams(query);
        const params = queryParams.set('project', projectId);
        return this.http.get<{ apps: App[]; embedded: Embedded }>(APP_SERVICE_API_ROOT, { params }).pipe(
            map(({ apps }) =>
                apps.map((businessProject) => new App(businessProject))
            ),
            catchError(this.errorService.errorHandler<App[]>(this.errors, 'Récupération des applications', []))
        );
    }

    public createApp(app: App): Observable<App> {
        return this.http.post<App>(APP_SERVICE_API_ROOT, app)
            .pipe(
                map(appCreated => new App(appCreated)),
                catchError(this.errorService.errorHandler<App>(this.errors, 'Création d\'une application', null))
            );
    }

    public updateApp(app: App): Observable<App> {
        return this.http.patch<App>(APP_SERVICE_API_ROOT + `/${app.id}`, app)
            .pipe(
                map(appUpdated => new App(appUpdated)),
                catchError(this.errorService.errorHandler<App>(this.errors, 'Modification d\'une application', null))
            );
    }
    public deleteApp(id): Observable<App> {
        return this.http.delete<App>(APP_SERVICE_API_ROOT + `/${id}`)
            .pipe(
                map(appDeleted => new App(appDeleted)),
                catchError(this.errorService.errorHandler<App>(this.errors, 'Suppression d\'une application', null))
            );
    }
}
